import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import {
  startOfLocalDay,
  startOfNextLocalDay,
  useLocalDayKey,
} from "@-/dev.improve.haniverse.lib.time.local-day";
import { reviewRechargePoints } from "#use-review-recharge-query";

/**
 * Streak points earned on the local day containing `now`: every live FSRS review log of that day,
 * Again +1 and Hard/Good/Easy +5, uncapped. The same day total the stats streak counts.
 */
export async function readStreakPointsForDay(db: ActorDatabaseHandle, now: Date): Promise<number> {
  const logs = await db.query.fsrsCardReviewLog.findMany({
    columns: { rating: true },
    where: {
      review: {
        gte: startOfLocalDay(now).toISOString(),
        lt: startOfNextLocalDay(now).toISOString(),
      },
    },
  });
  return logs.reduce((sum, log) => sum + reviewRechargePoints(log.rating), 0);
}

export function useGetStreakPointsTodayQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const dayKey = useLocalDayKey();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(["fsrs_card_review_log"]));
  return () => withTableChangeVersion(["streakPointsToday", { actorDb, dayKey }] as const);
}

/** Streak points earned today; the day keeps the streak at `STREAK_DAILY_RECHARGE_POINTS`. */
export const [useStreakPointsTodayQueryOptions] = createUseQueryOptions(
  useGetStreakPointsTodayQueryKey,
  async ({ queryKey: [_, { actorDb }] }) =>
    readStreakPointsForDay(actorDb(import.meta.location), new Date()),
);
